import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { doc, getDoc, collection, query, where, getDocs, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase'; 
import Layout from '../../components/ui/Layout';
import { useAuth } from '../../components/context/AuthContext';
import { getStorage, ref as storageFBRef, getDownloadURL } from 'firebase/storage';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import ShareButton from '../../components/ui/ShareButton';
import { SITE_URL, SITE_NAME, getProductShareUrlInSupermarket } from '../../constants/site';
import { Button, Container, Row, Col, Card, ListGroup, Spinner, Modal, Badge } from 'react-bootstrap';
import { createLogger } from '../../utils/logger';
import { useNotification } from '../../components/ui/Notification';
import { formatCartItems, calculateTotalItems, calculateTotalPrice, getActiveCartInstanceId } from '../../utils/cartHelpers';
import {
  effectiveDisplayPrice,
  priceForProductType,
  quantityForType,
  discountDealsForType,
  typePriceFromMap,
} from '../../utils/inventoryPricing';

const logger = createLogger('ProductDetails');

// ProductImage Helper Component
const ProductImage = ({ imagePath, altText, className, style, onClick }) => {
  const [imageUrl, setImageUrl] = useState('');
  const [error, setError] = useState(false);
  const storage = getStorage();

  useEffect(() => {
    setImageUrl(''); 
    setError(false);
    if (imagePath && typeof imagePath === 'string' && !imagePath.startsWith('blob:') && !imagePath.startsWith('http')) {
      const imageRef = storageFBRef(storage, imagePath);
      getDownloadURL(imageRef)
        .then(setImageUrl)
        .catch(err => {
          logger.error(`[ProductImage Helper] Error fetching image URL for GCS path "${imagePath}":`, err);
          setError(true);
        });
    } else if (imagePath && typeof imagePath === 'string' && imagePath.startsWith('http')) {
      setImageUrl(imagePath);
    } else if (imagePath) {
      logger.warn("[ProductImage Helper] Invalid imagePath (e.g., blob or not a string):", imagePath);
      setError(true);
    }
  }, [imagePath, storage]);

  const defaultStyle = { height: '200px', backgroundColor: '#f0f0f0', display:'flex', alignItems:'center', justifyContent:'center' };

  if (error) {
    return <div className={className || ""} style={{...defaultStyle, ...style}} onClick={onClick}><span>Image Error</span></div>;
  }
  if (!imageUrl) {
    return <div className={className || ""} style={{...defaultStyle, ...style}} onClick={onClick}><span>Loading...</span></div>;
  }
  return <img src={imageUrl} alt={altText} className={className || ""} style={style} onClick={onClick} onError={() => setError(true)} />;
};


const ProductDetailsPage = ({ productIdProp }) => {
  const { hardwareId, slug } = useParams();
  const supermarketId = hardwareId;
  const effectiveProductId = productIdProp ?? slug; 
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [inventoryDetails, setInventoryDetails] = useState(null);
  const [supermarketName, setSupermarketName] = useState('');
  const [loading, setLoading] = useState(true);
  const [errorPage, setErrorPage] = useState(null);
  const { currentUser } = useAuth();
  const { showNotification } = useNotification();
  
  // Discount deals modal state
  const [showDealsModal, setShowDealsModal] = useState(false);
  const [selectedProductDeals, setSelectedProductDeals] = useState([]);
  const [selectedProductName, setSelectedProductName] = useState('');
  const [selectedProductPrice, setSelectedProductPrice] = useState(0);
  const [productImageUrl, setProductImageUrl] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState(null);

  const breadcrumbItems = product ? [
    { path: "/", label: "Home" },
    { path: `/hardware/${supermarketId}/products`, label: supermarketName || "Products" },
    { label: product.display_name || "Product Details" }
  ] : [{ path: "/", label: "Home" }];

  // Handle viewing discount deals
  const handleViewDeals = () => {
    const deals = discountDealsForType(inventoryDetails, selectedTypeId);
    setSelectedProductDeals(deals);
    setSelectedProductName(product?.display_name || 'Unknown Product');
    const types = product?.product_types || [];
    const tp = inventoryDetails?.type_prices || {};
    const hasPricedTypes = types.some((t) => t.id && typePriceFromMap(tp, t.id) != null);
    const unit = hasPricedTypes
      ? priceForProductType(inventoryDetails, selectedTypeId)
      : effectiveDisplayPrice(inventoryDetails);
    setSelectedProductPrice(Number(unit) || 0);
    setShowDealsModal(true);
  };

  const handleCloseDealsModal = () => {
    setShowDealsModal(false);
    setSelectedProductDeals([]);
    setSelectedProductName('');
    setSelectedProductPrice(0);
  };

  const handleAddToCart = useCallback(async () => {
    if (!product || !inventoryDetails || !currentUser) {
      alert("Product details not fully loaded or user not logged in.");
      navigate(`/login?path=${  window.location.pathname}`);
      return;
    }
    const typesOnProduct = product.product_types || [];
    const tp = inventoryDetails.type_prices || {};
    const hasPricedTypes = typesOnProduct.some((t) => t.id && typePriceFromMap(tp, t.id) != null);
    const stockForLine = hasPricedTypes
      ? quantityForType(inventoryDetails, selectedTypeId)
      : quantityForType(inventoryDetails, null);
    if (stockForLine <= 0) {
      alert('This product is out of stock.');
      return;
    }
    let linePrice;
    let typeMeta;
    if (hasPricedTypes) {
      const unitForLine = typePriceFromMap(tp, selectedTypeId);
      if (!selectedTypeId || unitForLine == null) {
        alert('Please select a product type.');
        return;
      }
      linePrice = unitForLine;
      typeMeta = typesOnProduct.find((t) => t.id === selectedTypeId);
    } else {
      linePrice = effectiveDisplayPrice(inventoryDetails);
      if (linePrice == null || Number.isNaN(linePrice)) {
        alert('Price is not available for this product.');
        return;
      }
    }
    const displayLineName = typeMeta ? `${product.display_name} — ${typeMeta.name}` : product.display_name;
    const userId = currentUser.uid;
    const cartRef = doc(db, 'Carts', userId);
    try {
      let updatedCartData = null;
      
      await runTransaction(db, async (transaction) => {
        const cartDoc = await transaction.get(cartRef);
        const cartData = cartDoc.exists() ? cartDoc.data() : {};
        
        let supermarketCartInstances = cartData[supermarketId] || {};
        if (typeof supermarketCartInstances !== 'object' || Array.isArray(supermarketCartInstances) || supermarketCartInstances === null) {
            supermarketCartInstances = {};
        }
        let activeCartInstanceId = null;
        let activeCartInstance = null;
        for (const instanceId in supermarketCartInstances) {
          if (supermarketCartInstances.hasOwnProperty(instanceId) && supermarketCartInstances[instanceId] && supermarketCartInstances[instanceId].status === 'active') {
            activeCartInstanceId = instanceId;
            activeCartInstance = supermarketCartInstances[instanceId];
            break;
          }
        }
        const newProductEntry = {
          productId: effectiveProductId,
          quantity: 1,
          name: displayLineName,
          price: linePrice,
          ...(hasPricedTypes && selectedTypeId ? { productTypeId: selectedTypeId, productTypeName: typeMeta?.name } : {}),
        };
        if (activeCartInstance) {
          const productIndex = activeCartInstance.products.findIndex(
            (p) => p.productId === effectiveProductId && (p.productTypeId || '') === (hasPricedTypes ? selectedTypeId : '')
          );
          if (productIndex > -1) activeCartInstance.products[productIndex].quantity += 1;
          else activeCartInstance.products.push(newProductEntry);
          activeCartInstance.lastUpdatedAt = serverTimestamp();
          // Mark cart instance as having unseen updates for notification
          activeCartInstance.hasUnseenUpdate = true;
        } else {
          activeCartInstanceId = `cart_instance_${Date.now()}`;
          activeCartInstance = {
            instanceId: activeCartInstanceId,
            products: [newProductEntry],
            status: 'active',
            supermarketName: supermarketName, 
            createdAt: serverTimestamp(),
            lastUpdatedAt: serverTimestamp(),
            hasUnseenUpdate: true, // Mark new cart instance as having unseen updates
          };
        }
        supermarketCartInstances[activeCartInstanceId] = activeCartInstance;
        const updateData = { ...cartData, [supermarketId]: supermarketCartInstances, lastGlobalUpdate: serverTimestamp() };
        if (!cartDoc.exists()) transaction.set(cartRef, updateData);
        else transaction.update(cartRef, updateData);
        
        // Store only the active cart instance data for notification
        updatedCartData = activeCartInstance ? { [activeCartInstanceId]: activeCartInstance } : {};
      });
      
      // Find the added item's new quantity
      const addedItem = {
        name: displayLineName,
        quantity: 1,
        price: linePrice
      };
      
      // Get active cart instance ID for checkout
      const activeCartInstanceId = getActiveCartInstanceId(updatedCartData);
      
      // Show enhanced notification
      showNotification({
        type: 'cart',
        title: 'Item Added to Cart',
        message: '',
        duration: 6000, // 6 seconds
        cartData: {
          addedItem,
          cartItems: formatCartItems(updatedCartData),
          totalItems: calculateTotalItems(updatedCartData),
          totalPrice: calculateTotalPrice(updatedCartData),
          supermarketName: supermarketName
        },
        onViewCart: () => navigate('/carts'),
        onCheckout: () => {
          if (activeCartInstanceId) {
            navigate(`/checkout/${supermarketId}/${activeCartInstanceId}`);
          } else {
            navigate(`/checkout/${supermarketId}`);
          }
        },
        userId: currentUser.uid,
        supermarketId: supermarketId
      });
    } catch (error) {
      logger.error("Error adding to cart: ", error);
      alert(`Failed to add product to cart. ${  error.message}`);
    }
  }, [currentUser, product, inventoryDetails, supermarketId, supermarketName, navigate, effectiveProductId, showNotification, selectedTypeId]);

  useEffect(() => {
    if (!product) return;
    const types = product.product_types || [];
    if (!types.length) {
      setSelectedTypeId(null);
      return;
    }
    const invTp = inventoryDetails?.type_prices || {};
    const withPrice = types.filter((t) => t.id && typePriceFromMap(invTp, t.id) != null);
    if (withPrice.length === 0) {
      setSelectedTypeId(null);
      return;
    }
    setSelectedTypeId((prev) => (prev && withPrice.some((t) => t.id === prev) ? prev : withPrice[0].id));
  }, [product, inventoryDetails]);

  useEffect(() => {
    const fetchData = async () => {
      if (!effectiveProductId || !supermarketId) {
        setErrorPage('Missing product or supermarket ID.');
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const productRef = doc(db, 'Products', effectiveProductId);
        const productSnap = await getDoc(productRef);
        let fetchedProduct;
        if (productSnap.exists()) {
          fetchedProduct = { id: productSnap.id, ...productSnap.data() };
          setProduct(fetchedProduct);
        } else {
          setErrorPage('Product not found.');
          setLoading(false);
          return;
        }

        const supermarketRef = doc(db, 'Supermarkets', supermarketId);
        const supermarketSnap = await getDoc(supermarketRef);
        if (supermarketSnap.exists()) {
          setSupermarketName(supermarketSnap.data().display_name || 'Unknown hardware store');
        } else {
          setSupermarketName('An Unnamed Supermarket'); 
        }

        const inventoryQuery = query(collection(db, 'Inventory'), where("supermarket_id", "==", supermarketId), where("product_id", "==", effectiveProductId));
        const inventorySnapshot = await getDocs(inventoryQuery);
        if (!inventorySnapshot.empty) {
          setInventoryDetails(inventorySnapshot.docs[0].data());
        } else {
          setInventoryDetails({ quantity: 0, sale_price: null, type_prices: {} });
        }
      } catch (err) {
        logger.error("Error fetching data:", err);
        setErrorPage('Failed to load page details.');
      }
      setLoading(false);
    };
    fetchData();
  }, [effectiveProductId, supermarketId]);

  // Fetch product image URL for SEO/social sharing
  useEffect(() => {
    if (!product?.media?.[0]?.url || product.media[0].url.startsWith('http')) return;
    const storage = getStorage();
    const imageRef = storageFBRef(storage, product.media[0].url);
    getDownloadURL(imageRef)
      .then(setProductImageUrl)
      .catch(() => setProductImageUrl(''));
  }, [product?.media]);

  if (loading) return <Layout><Container className="mt-4 text-center"><Spinner animation="border" /> <p>Loading details...</p></Container></Layout>;
  if (errorPage) return <Layout><Container className="mt-4 text-center" ><p style={{ color: 'red' }}>{errorPage}</p></Container></Layout>;
  if (!product) return <Layout><Container className="mt-4 text-center"><p>Product not found.</p></Container></Layout>;

  const { display_name, description, attributes, media, product_types: productTypesList = [] } = product;
  const invTp = inventoryDetails?.type_prices || {};
  const offeredTypes = (productTypesList || []).filter(
    (t) => t.id && typePriceFromMap(invTp, t.id) != null
  );
  const displayUnitPrice =
    offeredTypes.length > 0
      ? priceForProductType(inventoryDetails, selectedTypeId)
      : effectiveDisplayPrice(inventoryDetails);
  const priceLabel =
    displayUnitPrice != null && !Number.isNaN(displayUnitPrice) ? displayUnitPrice.toFixed(2) : 'N/A';

  const lineStock =
    offeredTypes.length > 0
      ? selectedTypeId
        ? quantityForType(inventoryDetails, selectedTypeId)
        : null
      : quantityForType(inventoryDetails, null);
  const stockLabel =
    offeredTypes.length > 0
      ? selectedTypeId == null
        ? 'Select a type to see stock'
        : lineStock !== null && lineStock !== undefined
          ? String(lineStock)
          : 'N/A'
      : String(quantityForType(inventoryDetails, null));
  const dealsForSelection = discountDealsForType(inventoryDetails, selectedTypeId);
  const bulkDealsCountAcrossTypes = offeredTypes.reduce(
    (sum, t) => sum + discountDealsForType(inventoryDetails, t.id).length,
    0
  );
  const bulkDealsOnOtherTypes =
    dealsForSelection.length === 0 && bulkDealsCountAcrossTypes > 0;

  const seoTitle = `${display_name} at ${supermarketName} | ${SITE_NAME}`;
  const seoDescription = description || `${display_name} - Shop at ${supermarketName}. Price: $${priceLabel}.`;
  const seoUrl = getProductShareUrlInSupermarket(product, supermarketId);
  const seoImage = productImageUrl || `${SITE_URL}/logo192.png`;

  return (
    <Layout>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="product:price:amount" content={displayUnitPrice != null ? displayUnitPrice : ''} />
        <meta property="product:price:currency" content="JMD" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: display_name,
            description: description || seoDescription,
            image: seoImage,
            offers: {
              '@type': 'Offer',
              price: displayUnitPrice != null ? displayUnitPrice : undefined,
              priceCurrency: 'JMD',
              availability: inventoryDetails?.quantity > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
            },
          })}
        </script>
      </Helmet>
      <Container className="mt-4" style={{ maxWidth: '900px'}}>
        <Row className="align-items-center mb-1">
            <Col xs="auto">
                <Button variant="outline-secondary" onClick={() => navigate(-1)} className="mb-2">
                &larr; Go Back
                </Button>
            </Col>
            <Col>
                <BreadcrumbNavigation items={breadcrumbItems} />
            </Col>
        </Row>

        {supermarketName && 
          <p className="text-muted">From: <Link to={`/hardware/${supermarketId}/products`}><strong>{supermarketName}</strong></Link></p>
        }
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <h1 className="mb-0">{display_name || 'Product Name Not Available'}</h1>
          <ShareButton
            url={seoUrl}
            title={seoTitle}
            text={seoDescription}
            variant="outline-secondary"
            size="sm"
          />
        </div>
        
        <Row className="mb-4">
            <Col md={6} className="mb-3 mb-md-0">
                {media && media.length > 0 && media[0] && media[0].url ? (
                    <ProductImage 
                        imagePath={media[0].url} 
                        altText={display_name || "Main product image"}
                        style={{ width: '100%', maxHeight: '400px', objectFit: 'contain', border:'1px solid #eee', borderRadius:'4px'}}
                    />
                ) : (
                    <div style={{width: '100%', height: '300px', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius:'4px' }}><span>No Image Available</span></div>
                )}
            </Col>
            <Col md={6}>
                {inventoryDetails && (
                <div className="mb-3">
                    {offeredTypes.length > 0 && (
                      <div className="mb-3">
                        <p className="fw-semibold mb-2">Choose type</p>
                        <div className="d-flex flex-wrap gap-2">
                          {offeredTypes.map((t) => (
                            <Button
                              key={t.id}
                              type="button"
                              variant={selectedTypeId === t.id ? 'primary' : 'outline-primary'}
                              size="sm"
                              className="d-flex align-items-center gap-2"
                              onClick={() => setSelectedTypeId(t.id)}
                            >
                              {t.image?.url && (
                                <ProductImage
                                  imagePath={t.image.path || t.image.url}
                                  altText=""
                                  style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4 }}
                                />
                              )}
                              <span>{t.name}</span>
                              <span className="text-nowrap">${typePriceFromMap(invTp, t.id).toFixed(2)}</span>
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                    <p className="lead fs-3">Price: ${priceLabel}</p>
                    <p className="text-muted">Stock: {stockLabel}</p>
                    {dealsForSelection.length > 0 && (
                      <div className="mt-3">
                        <div className="d-flex align-items-center mb-2">
                          <Badge bg="success" className="me-2">
                            {dealsForSelection.length} Deal{dealsForSelection.length !== 1 ? 's' : ''} Available
                          </Badge>
                          <Button variant="outline-success" size="sm" onClick={handleViewDeals}>
                            View Deals
                          </Button>
                        </div>
                        <small className="text-muted">
                          Bulk purchase discounts for this size — open View Deals to see savings.
                        </small>
                      </div>
                    )}
                    {bulkDealsOnOtherTypes && (
                      <div className="mt-3">
                        <Badge bg="info" className="me-2 text-dark">
                          {bulkDealsCountAcrossTypes} bulk deal{bulkDealsCountAcrossTypes !== 1 ? 's' : ''} on other sizes
                        </Badge>
                        <div className="small text-muted mt-1">
                          Select a different type above to view bulk pricing for that size.
                        </div>
                      </div>
                    )}
                </div>
                )}
                {description && <p className="mb-4">{description}</p>}
                <Button
                  onClick={handleAddToCart}
                  variant="primary"
                  size="lg"
                  className="w-100 mb-2"
                  disabled={
                    !inventoryDetails ||
                    !currentUser ||
                    (offeredTypes.length > 0 && !selectedTypeId) ||
                    (offeredTypes.length > 0 && selectedTypeId && (lineStock ?? 0) <= 0) ||
                    (offeredTypes.length === 0 && (lineStock ?? 0) <= 0)
                  }
                >
                {currentUser
                  ? offeredTypes.length > 0 && !selectedTypeId
                    ? 'Select type'
                    : (lineStock ?? 0) > 0
                      ? 'Add to Cart'
                      : 'Out of Stock'
                  : 'Login to Add'}
                </Button>
            </Col>
        </Row>

        {media && media.length > 1 && (
          <Card className="mb-4 product-media-section">
            <Card.Header><h4>More Images</h4></Card.Header>
            <Card.Body>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'flex-start' }}>
                {media.slice(1).map((item, index) => ( 
                  <div key={index} className="media-item-thumb" style={{ border: '1px solid #ddd', padding: '5px', borderRadius: '4px' }}>
                    <ProductImage 
                      imagePath={item.url}
                      altText={item.name || `Product image ${index + 2}`}
                      style={{ width: '100px', height: '100px', objectFit: 'contain' }}
                    />
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}

        {attributes && attributes.length > 0 && (
          <Card className="mb-4 product-attributes-section">
            <Card.Header><h4>Product Attributes</h4></Card.Header>
            <ListGroup variant="flush">
              {attributes.map((attr, index) => (
                <ListGroup.Item key={index}>
                  <strong>{attr.name || 'N/A'}:</strong> {attr.value || 'N/A'}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Card>
        )}
      </Container>

             {/* Discount Deals Modal */}
       <Modal show={showDealsModal} onHide={handleCloseDealsModal} size="lg">
         <Modal.Header closeButton>
           <Modal.Title>Discount Deals for {selectedProductName}</Modal.Title>
         </Modal.Header>
         <Modal.Body>
           {selectedProductDeals.length === 0 ? (
             <p>No discount deals currently available for this item.</p>
           ) : (
             <div>
               <p className="text-muted mb-3">
                 <strong>Regular Price:</strong> ${selectedProductPrice.toFixed(2)} per item
               </p>
               <ListGroup>
                 {selectedProductDeals.map((deal, index) => {
                   const regularPrice = selectedProductPrice * deal.quantity;
                   const savings = regularPrice - deal.price;
                   const savingsPercentage = ((savings / regularPrice) * 100).toFixed(1);
                   return (
                     <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                       <div className="flex-grow-1">
                         <div className="d-flex justify-content-between align-items-start mb-2">
                           <div>
                             <strong className="fs-5">{deal.quantity} items</strong>
                             <br />
                             <span className="text-success fw-bold fs-4">${deal.price.toFixed(2)}</span>
                           </div>
                           <Badge bg="success" className="fs-6">
                             Save {savingsPercentage}%
                           </Badge>
                         </div>
                         <div className="text-muted small">
                           <div>Regular price: ${regularPrice.toFixed(2)}</div>
                           <div>You save: <span className="text-success fw-bold">${savings.toFixed(2)}</span></div>
                           <div>Per item: <span className="text-success fw-bold">${(deal.price / deal.quantity).toFixed(2)}</span></div>
                         </div>
                       </div>
                     </ListGroup.Item>
                   );
                 })}
               </ListGroup>
               <div className="mt-3 p-3 bg-light rounded">
                 <small className="text-muted">
                   <i className="fas fa-info-circle me-1"></i>
                   These deals are automatically applied when you add the specified quantity to your cart.
                 </small>
               </div>
             </div>
           )}
         </Modal.Body>
         <Modal.Footer>
           <Button variant="secondary" onClick={handleCloseDealsModal}>Close</Button>
         </Modal.Footer>
       </Modal>
    </Layout>
  );
};

export default ProductDetailsPage;
